import {
  REFRESH_TOKEN_STORAGE_KEY,
  useAuthContext,
} from '../../context/auth/AuthContextProvider'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { jwtDecoder } from '../../utils/helpers/helper'
import { axiosBase } from '../axios'

// Refresh tokens are single use, so requests that 401 together must share one refresh call.
let pendingRefresh: Promise<string | null> | null = null

const useRefreshToken = () => {
  const { userSignIn } = useAuthContext()
  const refreshTokenStorage = useLocalStorage(REFRESH_TOKEN_STORAGE_KEY)
  const tokenStorage = useLocalStorage('cl_user')

  const refresh = () => {
    // Read from storage, not memory: another tab may have used and replaced the token
    const refreshToken = refreshTokenStorage.getItem()
    if (!refreshToken) {
      return Promise.resolve(null)
    }

    if (!pendingRefresh) {
      pendingRefresh = axiosBase
        .post(
          '/api/token/refresh',
          JSON.stringify({ refresh_token: refreshToken })
        )
        .then((response) => {
          // Re-decode so claims that changed since sign in (roles, passwordResetAt...) are picked up
          const refreshedUser = jwtDecoder(response.data.token)
          if (!refreshedUser) {
            return null
          }

          userSignIn({
            ...refreshedUser,
            refreshToken: response.data.refresh_token ?? '',
          })

          return response.data.token as string
        })
        .catch((_) => {
          // Another tab refreshed with the same single-use token first, take over its session
          const storedRefreshToken = refreshTokenStorage.getItem()
          const storedUser = jwtDecoder(tokenStorage.getItem())
          if (
            storedRefreshToken &&
            storedRefreshToken !== refreshToken &&
            storedUser
          ) {
            userSignIn({ ...storedUser, refreshToken: storedRefreshToken })

            return storedUser.token
          }

          return null
        })
        .finally(() => {
          pendingRefresh = null
        })
    }

    return pendingRefresh
  }

  return refresh
}

export default useRefreshToken
